import { useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth, user } from 'src/backend';
// redux
import {
  login,
  register,
  logout,
  setUserData,
  setSession,
  setSuppressionStatus,
} from '../redux/slices/auth';
import { setUserDocument } from '../redux/slices/document';
import moment from 'moment-timezone';
import useLocales from './useLocales';
import * as Sentry from '@sentry/react';
import { firebaseSignOut } from 'src/adapters/FirestoreAdapter';
import { resetPageFilters } from 'src/hooks/pageFiltersStore';
import {
  getPlanBySubscriptionProductId,
  hasPlanCapability,
  isOpsPlan,
  isFoodMoverLitePlan,
  normalizePlanKey,
  hasEffectiveHomeDelivery,
  formatDisplayDate,
} from 'careit';
// ----------------------------------------------------------------------

export const getActivePlan = (user) => {
  const planActive = user?.subscription_status?.toUpperCase() === 'ACTIVE';
  const rawPlan =
    getPlanBySubscriptionProductId(user?.subscription_product_id) ?? 'free';
  // Legacy *Home product IDs normalize to base plan (Lite/Pro)
  const { planKey } = normalizePlanKey(rawPlan);
  return planActive ? planKey : 'free';
};

export default function useAuth() {
  const dispatch = useDispatch();
  const {
    user: userData,
    impersonation,
    isLoading,
    isAuthenticated,
    isOrgModerationEnabled,
    isEmailSuppressed,
  } = useSelector((state) => state.auth);
  const { onChangeLang } = useLocales();

  const timezoneId = useMemo(
    () =>
      userData?.user_timezone_id ||
      userData?.timezone_id ||
      'America/Los_Angeles',
    [userData]
  );

  useEffect(() => {
    if (userData?.lang) {
      onChangeLang(userData.lang);
    }
  }, [userData?.lang]);

  //format date to user's timezone
  const fDateTimeInUserTimeZone = useCallback(
    (date) => {
      return formatDisplayDate(
        moment.utc(date).tz(timezoneId),
        'weekdayDateLong',
        { time: true }
      );
    },
    [timezoneId]
  );

  const planActive = userData?.subscription_status?.toUpperCase() === 'ACTIVE';
  const rawPlanKey = planActive
    ? (getPlanBySubscriptionProductId(userData?.subscription_product_id) ??
      'free')
    : 'free';
  const { planKey: plan, homeDeliveryAddon: legacyBundleAddon } =
    normalizePlanKey(rawPlanKey);

  const homeDeliveryAddon = Boolean(
    legacyBundleAddon ||
    userData?.home_delivery_addon === true ||
    userData?.home_delivery_addon === 1
  );
  const distributionsEnabled = Boolean(
    userData?.distributions_enabled === true ||
    userData?.distributions_enabled === 1
  );

  // Ops org: food runs and/or home delivery (any paid rescue/delivery plan)
  const isFoodMover = isOpsPlan(plan, { homeDeliveryAddon });
  // Pro-class extras (fleet, tax receipts, training, donations report)
  const isFoodMoverFull = hasPlanCapability(plan, 'proOps');
  // Lite-class (incl. Lite + Home Delivery add-on)
  const isFoodMoverLite = isFoodMoverLitePlan(plan);
  const hasFoodRuns = hasPlanCapability(plan, 'foodRuns');
  // FDP plan, HD Stripe add-on flag, legacy *Home SKU, or distributions_enabled
  const hasHomeDelivery = hasEffectiveHomeDelivery(rawPlanKey, {
    homeDeliveryAddon,
    distributionsEnabled,
  });

  return {
    user: userData,
    impersonation,
    isImpersonating: Boolean(impersonation),
    plan,
    isFoodMover,
    isFoodMoverFull,
    isFoodMoverLite,
    hasFoodRuns,
    hasHomeDelivery,
    homeDeliveryAddon,
    isLoading,
    isAuthenticated,
    isOrgModerationEnabled,
    isEmailSuppressed,
    fDateTimeInUserTimeZone,
    login: ({ email, password, teamId, then, except }) =>
      dispatch(
        login({
          email,
          password,
          teamId,
          then,
          except,
        })
      ),

    register: (userData, enqueueSnackbar, setData, then, except, options) =>
      dispatch(
        register(userData, enqueueSnackbar, setData, then, except, options)
      ),

    logout: () => {
      firebaseSignOut();
      dispatch(setUserDocument({ document: {} }));
      dispatch(logout());
      resetPageFilters();
      Sentry.setUser(null);
    },

    resetPassword: auth.resetPasswordOTP,

    updateProfile: async (data) => {
      await user.updateUser(data);
      let userData = await auth.getUserData();
      dispatch(setUserData({ user: userData }));
    },

    updateBusiness: async (data) => {
      await user.updateBusiness(data);
      let userData = await auth.getUserData();
      dispatch(setUserData({ user: userData }));
    },
    invalidateUserData: async () => {
      let userData = await auth.getUserData();
      dispatch(setUserData({ user: userData }));
    },
    verifyEmail: (data) => auth.verifyEmail(data),
    getSuppressionStatus: (data) => {
      const suppressionStatus = auth.getSuppressionStatus(data);
      dispatch(setSuppressionStatus({ isEmailSuppressed: suppressionStatus }));
    },
    resendEmailVerification: (data) => auth.resendEmailVerification(data),
    sendVerificationToUnsuppressEmail: (data) =>
      auth.sendVerificationToUnsuppressEmail(data),
    setUserData: (data, reload = true) => {
      setSession(data.token, data.refreshToken);
      if (reload) {
        window.location.reload();
      }
    },
  };
}
